import {
    Box,
    Grid,
    InternalStandardProps,
    Theme,
    Typography,
} from "@mui/material";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useGlobalAudios } from "JS/React/Hooks/Audio";
import { useGlobalGifts } from "JS/React/Hooks/Gifts";
import { useGlobalPlaylists, usePlaylistResume } from "JS/React/Hooks/Playlist";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import qs from "qs";
import { createStyles, makeStyles } from "@mui/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { MediaPlayer } from "JS/React/Components/MediaPlayer";
import {
    sortArrayByKey,
    shuffle,
    getDownloadedAudios,
    setPlayedTime,
} from "JS/Helpers";
import {
    Content,
    ContentWithMedia,
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
    GiftWithMedia,
    ResumeContent,
} from "JS/Models";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import { useGlobalPlaylistControls } from "JS/React/Hooks/PlaylistControls";
import moment from "moment";
import { getDexieConnectionRefresh } from "JS/Database/Dexie";
import { config } from "JS/Config";
import {
    useContentPlayingHandlers,
    usePlayerHandlers,
} from "JS/React/Hooks/MediaPlayer";
import { useMiniPlayerHandlers } from "JS/React/Hooks/MiniPlayer";
import { PlayerActionBtns } from "JS/React/Components/MediaPlayer/PlayerActionBtns";
import { PLAYER_PROGRESS_INTERVAL } from "JS/Helpers/Constants";
import {
    SentGiftType,
    getPlayerPlayedTimeToLog,
    toSentGift,
} from "JS/Models/Firebase/GiftDashboard";
import { useGiftDashboard } from "JS/React/Hooks/Firebase/GiftDashboard";
import { usePlaybackAudios } from "JS/Helpers/PlaylistHelpers";

export interface PlaylistPlayerProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const PlaylistPlayer = (props: PlaylistPlayerProps) => {
    const classes = useStyles(props);
    const playerRef = useRef<any>();
    const { handleChangeSeek } = usePlayerHandlers(playerRef);
    const [isReady, setIsReady] = useState(false);
    const [isReadyToPlay, setIsReadyToPlay] = useState(false);
    const [audiosToPlay, setAudiosToPlay] =
        useState<(ContentWithMedia | GiftWithMedia)[]>();
    const [isSingleLoop, setSingleLoop] = useState(false);
    const [contentUrl, setContentUrl] = useState<string>("");

    const [currentPlayingState, setCurrentPlayingState] = useState(true);
    const { handlePlayerPause, handlePlayerPlay } = useContentPlayingHandlers(
        setCurrentPlayingState,
    );

    const {
        audios: allAudios,
        resumeUserAudios,
        resumeUserBundles,
        updateResumeBundles,
        setGlobalAudios,
        updateRecentlyPlayed,
        updateResumeAudios,
    } = useGlobalAudios();
    const { userPlaylists: playlists } = useGlobalPlaylists();
    const { receivedMemberAudios, receivedProspectAudios, setGlobalGifts } =
        useGlobalGifts();
    const params: any = useParams();
    const history = useHistory();
    const { linkProvider } = useRouting();
    const { id } = params;
    const location = useLocation();
    const [forceReloadKey, setForceReloadKey] = useState(0);

    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const {
            index = 0,
            bundleIndex = 0,
            scrollTo = "0",
        } = parsedQueryString;

        return {
            index: index.toString(),
            bundleIndex: bundleIndex.toString(),
            scrollTo: scrollTo.toString(),
        };
    }, [location?.search]);
    const setQueryParams = (params: Partial<typeof queryParams>) => {
        history.replace({
            pathname: location.pathname,
            search: qs.stringify({ ...queryParams, ...params }),
        });
    };
    const getIndexByNid = (nid: string) =>
        audiosToPlay?.findIndex((elem) => elem.nid === nid);

    const mediaIndex = +queryParams.index;
    const bundleIndex = +queryParams.bundleIndex;
    useEffect(() => {
        if (!!audiosToPlay?.length) {
            setQueryParams({
                ...queryParams,
                index: getIndexByNid(queryParams.scrollTo).toString(),
            });
        }
    }, [queryParams.scrollTo, audiosToPlay]);

    const playlist = useMemo(
        () => playlists?.find((p) => p?.id === +id),
        [playlists, history, id, linkProvider.react, mediaIndex],
    );

    const { playlistControls, toggleGlobalShuffle, toggleGlobalLoop } =
        useGlobalPlaylistControls();

    useEffect(() => {
        if (audiosToPlay?.length - 1 < mediaIndex) {
            setQueryParams({
                ...queryParams,
                index: "0",
                bundleIndex: "0",
            });
        }
    }, [audiosToPlay]);

    const {
        playlistAudios,
        currentAudio,
        isGift,
        getAudioByIndex,
        sortedMappedAudios,
        title,
    } = usePlaybackAudios(
        playlist,
        allAudios,
        playlistControls.shuffle,
        mediaIndex,
        bundleIndex,
        setQueryParams,
        true,
    );

    useEffect(() => {
        let playlist: (ContentWithMedia | GiftWithMedia)[] = [];
        if (playlistControls.shuffle && !!playlistAudios?.length) {
            const current = playlistAudios[mediaIndex];
            const shuffled = shuffle(
                playlistAudios.filter((a) => a.nid !== current?.nid),
            );
            const newPlaylist: (ContentWithMedia | GiftWithMedia)[] = [];
            newPlaylist.push(current);
            shuffled.forEach((a) => newPlaylist.push(a));
            playlist = [...newPlaylist];
            setQueryParams({
                index: "0",
                bundleIndex: bundleIndex.toString(),
            });
        } else playlist = playlistAudios;
        mapDownloadedAudios(playlist);
    }, [playlistAudios, playlistControls.shuffle]);

    const mapDownloadedAudios = async (
        playlistData: (ContentWithMedia | GiftWithMedia)[],
    ) => {
        await getDownloadedAudios(playlistData)
            .then((res) => {
                if (res?.length) {
                    setAudiosToPlay(
                        playlistData
                            .map((a) => res.find((r) => r.nid === a.nid))
                            .filter((a) => a !== undefined),
                    );
                }
            })
            .catch((err) => {
                console.log({ err });
            });
    };

    const next = () => {
        const isBundle = currentAudio?.media?.length > 1;
        setIsReady(false);
        setIsReadyToPlay(false);

        if (playlistAudios?.length === 1) {
            setForceReloadKey((prev) => prev + 1);
            return;
        }

        setCurrentPlayingState(true);
        setQueryParams({
            index:
                isBundle && bundleIndex < currentAudio?.media?.length - 1
                    ? mediaIndex.toString()
                    : ((mediaIndex + 1) % audiosToPlay?.length).toString(),
            bundleIndex:
                isBundle && bundleIndex < currentAudio?.media?.length - 1
                    ? (bundleIndex + 1).toString()
                    : "0",
        });
    };
    const previous = () => {
        const isBundle = currentAudio?.media?.length > 1;
        setIsReady(false);
        setIsReadyToPlay(false);

        if (playlistAudios?.length === 1) {
            setForceReloadKey((prev) => prev + 1);
            return;
        }

        let newMediaIndex = mediaIndex;
        if (mediaIndex === 0) {
            if (isBundle) {
                if (bundleIndex === 0) newMediaIndex = audiosToPlay?.length - 1;
                else newMediaIndex = mediaIndex;
            } else newMediaIndex = audiosToPlay?.length - 1;
        } else if (!isBundle || bundleIndex === 0)
            newMediaIndex = mediaIndex - 1;
        let newBundleIndex = bundleIndex;
        if (isBundle && bundleIndex !== 0) {
            newBundleIndex = bundleIndex - 1;
        } else if (newMediaIndex !== mediaIndex) {
            const prevAudio = getAudioByIndex(newMediaIndex);
            if (prevAudio?.media?.length > 1)
                newBundleIndex = prevAudio?.media?.length - 1;
            else newBundleIndex = 0;
        }
        setCurrentPlayingState(true);
        setQueryParams({
            index: newMediaIndex?.toString(),
            bundleIndex: newBundleIndex?.toString(),
        });
    };

    const { logFirebaseEvent } = useFirebaseLogger();

    const { miniPlayer } = useMiniPlayerHandlers();

    useEffect(() => {
        if (currentAudio?.media) {
            const isBundle = currentAudio.media.length > 1;

            const { nid, sku_id, title } = currentAudio;
            const action: FirebaseEventAction = {
                action: EventActions.PLAY,
                nId: nid,
                category: currentAudio.isReceived
                    ? EventCategories.GIFT_AUDIOS
                    : EventCategories.AUDIOS,
                contentTitle: isBundle
                    ? currentAudio?.media[bundleIndex]?.media_title
                    : title,
                skuId: isBundle
                    ? currentAudio.media[bundleIndex].media_file_name
                    : sku_id,
            };

            const getEventName = () =>
                currentAudio.isReceived
                    ? EventNames.GIFT_AUDIO_PLAYED
                    : EventNames.AUDIO_PLAYED;

            logFirebaseEvent(getEventName(), action);
        }
    }, [currentAudio, mediaIndex, bundleIndex]);

    useEffect(() => {
        if (currentAudio) {
            const { nid } = currentAudio;
            const isBundle = +currentAudio.no_of_files > 1;
            const contentNid = isBundle ? `${nid}-${bundleIndex + 1}` : nid;

            getDexieConnectionRefresh()
                .audios.get(`${config.user.memberId}-${contentNid}`)
                .then((res) => {
                    if (res) {
                        setContentUrl(URL.createObjectURL(res.blob));
                    }
                })
                .catch((err) => {
                    console.log({ err });
                });
        }
    }, [currentAudio, bundleIndex, playlist]);

    const onMediaPlayerReady = useCallback(() => {
        setIsReadyToPlay(true);
        if (audiosToPlay && audiosToPlay[mediaIndex]) {
            const isBundle = currentAudio.media.length > 1;

            if (!isReady && currentAudio) {
                if (isBundle) {
                    const resumeBundleData: ResumeContent = resumeUserBundles
                        ? resumeUserBundles.find(
                              (x) =>
                                  x.nid === currentAudio.nid &&
                                  x.title ===
                                      currentAudio.media[bundleIndex]
                                          ?.media_title,
                          )
                        : ({} as ResumeContent);
                    if (
                        resumeBundleData &&
                        Math.ceil(+resumeBundleData.lastPlayed) !==
                            Math.ceil(+resumeBundleData.duration)
                    ) {
                        playerRef.current.seekTo(
                            resumeBundleData.lastPlayed,
                            "seconds",
                        );
                    }
                } else {
                    const resumeData: ResumeContent = resumeUserAudios
                        ? resumeUserAudios.find(
                              (x) => x.nid === currentAudio?.nid,
                          )
                        : ({} as ResumeContent);
                    if (
                        resumeData &&
                        Math.ceil(+resumeData.lastPlayed) !==
                            Math.ceil(+resumeData.duration)
                    ) {
                        playerRef.current.seekTo(
                            resumeData.lastPlayed,
                            "seconds",
                        );
                    }
                }
                const data: ResumeContent = {
                    nid: currentAudio.nid,
                    sku_id: currentAudio.sku_id,
                    title: isBundle
                        ? currentAudio?.media[bundleIndex]?.media_title
                        : currentAudio?.title,
                    lastPlayed: playerRef.current.getCurrentTime(),
                    maximumPlayed: playerRef.current.getCurrentTime(),
                    duration: playerRef.current.getDuration(),
                };

                isBundle ? updateResumeBundles(data) : updateResumeAudios(data);
                handleRecentlyPlayed();
                setIsReady(true);
            }
        }
    }, [isReady, audiosToPlay, mediaIndex, bundleIndex]);

    const handleRecentlyPlayed = () => {
        const timeStamp = moment().unix().toString();

        if (allAudios?.filter((x) => x.nid === currentAudio?.nid).length > 0) {
            const toSaveAudios: Content[] = allAudios.map((d) => {
                if (d.nid === currentAudio?.nid) {
                    return {
                        ...d,
                        played: timeStamp,
                    };
                } else {
                    return d;
                }
            });
            setGlobalAudios(toSaveAudios);
        } else if (
            receivedMemberAudios?.filter(
                (video) => video.nid === currentAudio?.nid,
            ).length > 0
        ) {
            const toSaveReceivedMembers = receivedMemberAudios.map((d) =>
                setPlayedTime(currentAudio.nid, d, timeStamp),
            );
            setGlobalGifts({
                receivedMemberAudios: sortArrayByKey(
                    toSaveReceivedMembers,
                    "title",
                    "ASC",
                ),
            });
        } else {
            const toSaveProspectsMembers = receivedProspectAudios?.map((d) =>
                setPlayedTime(currentAudio.nid, d, timeStamp),
            );
            setGlobalGifts({
                receivedProspectAudios: sortArrayByKey(
                    toSaveProspectsMembers,
                    "title",
                    "ASC",
                ),
            });
        }
        updateRecentlyPlayed({ ...currentAudio, played: timeStamp });
    };

    const onPlayerProgress = async () => {
        updateResumeData({
            lastPlayed: playerRef.current.getCurrentTime(),
            maximumPlayed: playerRef.current.getCurrentTime(),
        });
    };

    const updateResumeData = (playedTime: {
        lastPlayed: string;
        maximumPlayed: string;
    }) => {
        if (currentAudio) {
            const isBundle = +currentAudio.no_of_files > 1;
            const data: ResumeContent = {
                nid: currentAudio.nid,
                sku_id: currentAudio.sku_id,
                title: isBundle
                    ? currentAudio?.media[bundleIndex]?.media_title
                    : currentAudio.title,
                lastPlayed: playedTime.lastPlayed,
                maximumPlayed: playedTime.maximumPlayed,
            };
            isBundle ? updateResumeBundles(data) : updateResumeAudios(data);
        }
    };

    const { savePlaylistResumeState } = usePlaylistResume(
        id,
        currentAudio?.nid,
        bundleIndex,
    );

    const isBundle = +currentAudio?.no_of_files > 1;

    const onComplete = () => {
        if (isReceivedMemberAudio) {
            let action = undefined;
            if (isBundle)
                action = {
                    index: bundleIndex + 1,
                };
            logGiftCompleted(action);
        }
        if (!isSingleLoop) {
            updateResumeData({
                lastPlayed: "0",
                maximumPlayed: "0",
            });
        }
        if (!playlistControls.loop && mediaIndex === audiosToPlay?.length - 1)
            savePlaylistResumeState({
                id,
                resumeAudioNid: currentAudio?.nid,
                resumeBundleIndex: 0,
            });
        if (playlistControls.loop || mediaIndex < audiosToPlay?.length - 1)
            next();
    };

    const giftDashboardData = useMemo(() => {
        return toSentGift(currentAudio);
    }, [currentAudio]);

    const getPlayerAction = () => {
        return getPlayerPlayedTimeToLog(
            playerRef,
            isBundle ? bundleIndex : null,
        );
    };

    const isReceivedMemberAudio = isGift(currentAudio?.nid);

    const { logGiftCompleted } = useGiftDashboard(
        giftDashboardData,
        SentGiftType.AUDIO,
        !!currentAudio && "sender" in currentAudio
            ? currentAudio?.sender?.member_id
            : null,
        isReceivedMemberAudio && currentPlayingState && !miniPlayer?.url,
        getPlayerAction,
        true,
    );

    return (
        <>
            <AppHeader
                title={title}
                canGoBack
                searchable={false}
                onBackClick={() => {
                    history.push(
                        linkProvider.react
                            .offline()
                            .playlist()
                            .detail(playlist.id.toString(), {
                                scrollTo: queryParams.scrollTo,
                            }),
                    );
                }}
            />
            <Grid container>
                <Grid item xs={12}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={"space-between"}
                    >
                        <Box
                            display={"flex"}
                            alignItems="center"
                            className={classes.forwardBackItem}
                            onClick={previous}
                        >
                            <FontAwesomeIcon
                                className={clsx(
                                    classes.fwdBackIcon,
                                    classes.backIcon,
                                )}
                                icon={["fas", "chevron-left"]}
                            />
                            <Typography
                                className={classes.fwdBackTypo}
                                fontWeight={"bold"}
                                variant="h5"
                            >
                                Previous
                            </Typography>
                        </Box>
                        <Box
                            display={"flex"}
                            alignItems="center"
                            className={classes.forwardBackItem}
                            onClick={next}
                        >
                            <Typography
                                className={classes.fwdBackTypo}
                                fontWeight={"bold"}
                                variant="h5"
                            >
                                Next
                            </Typography>
                            <FontAwesomeIcon
                                className={clsx(
                                    classes.fwdBackIcon,
                                    classes.fwdIcon,
                                )}
                                icon={["fas", "chevron-right"]}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <Grid item xs={12} className={classes.playerContainer}>
                <MediaPlayer
                    key={forceReloadKey}
                    title={title}
                    playerRef={playerRef}
                    mediaURL={contentUrl}
                    imageURL={""}
                    onReady={onMediaPlayerReady}
                    progressInterval={PLAYER_PROGRESS_INTERVAL}
                    onProgress={onPlayerProgress}
                    onEnded={onComplete}
                    loop={isSingleLoop}
                    isAudio={true}
                    onNext={next}
                    onPrevious={previous}
                    playing={currentPlayingState && !miniPlayer.url}
                    onPlay={handlePlayerPlay}
                    onPause={handlePlayerPause}
                    loading={!isReadyToPlay}
                    onUpdateExpiredUrl={() => {
                        setIsReady(false);
                        setIsReadyToPlay(true);
                    }}
                    onError={() => {
                        setIsReadyToPlay(true);
                    }}
                />
            </Grid>
            <Grid
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
                justifyContent={"center"}
            >
                <PlayerActionBtns
                    handleChangeSeek={handleChangeSeek}
                    isShuffle={playlistControls.shuffle}
                    isMultiLoop={playlistControls.loop}
                    isLoop={isSingleLoop}
                    onClickShuffle={() => {
                        setQueryParams({
                            ...queryParams,
                            index: sortedMappedAudios
                                .findIndex((a) => a.nid === currentAudio.nid)
                                .toString(),
                        });
                        toggleGlobalShuffle();
                        setIsReady(false);
                    }}
                    onClickMultiLoop={() => toggleGlobalLoop()}
                    onClickLoop={() => setSingleLoop(!isSingleLoop)}
                />
            </Grid>
            {audiosToPlay && (
                <Grid item xs={12} marginTop={1}>
                    <Typography className={classes.grey800} variant="body2">
                        {audiosToPlay[mediaIndex]?.description}
                    </Typography>
                </Grid>
            )}
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: theme.spacing(2),
        },

        grey500: {
            color: theme.palette.grey[500],
        },
        grey800: {
            color: theme.palette.grey[800],
        },
        greyA100: {
            color: theme.palette.grey.A100,
        },
        spinner: {
            color: theme.palette.primary.main,
        },
        icon: {
            color: theme.palette.grey[600],
            fontSize: "22px",
        },
        constMargin: {
            marginRight: theme.spacing(2),
        },
        playerContainer: {
            margin: theme.spacing(2, 0),
            height: "60vh",
            [theme.breakpoints.down("lg")]: {
                height: "50vh",
            },
            [theme.breakpoints.down("sm")]: {
                height: "30vh",
            },
        },
        graphicIcon: {
            color: theme.palette.grey[400],
        },
        graphicWrapper: {
            border: `5px solid ${theme.palette.common.white}`,
            borderRadius: "50%",
            height: "120px",
            width: "120px",
            marginRight: theme.spacing(2),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        dummyImageBox: {
            position: "relative",
            background: theme.palette.grey[900],
            height: "100%",
        },
        mediaImage: {
            width: "100%",
            height: "100%",
            objectFit: "contain",
            background: theme.palette.common.black,
        },
        date: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1),
        },
        transactionCompletePaper: {
            color: theme.palette.grey[600],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "120px",
        },
        transactionConfirmation: {
            color: theme.palette.grey[600],
            minHeight: "120px",
        },
        giftQuanityWrapper: {
            background: `${theme.palette.grey[300]} !important`,
            color: theme.palette.grey[600],
            borderRadius: "50%",
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            padding: theme.spacing(0.5),
            minWidth: "25px",
            minHeight: "25px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
        },
        giftSectionWarpper: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
        },
        giftIconButtonDisabled: {
            opacity: "50%",
            cursor: "not-allowed !important",
        },
        giftIconPositioner: {
            position: "absolute",
            bottom: 10,
            right: 10,
        },
        metaData: {
            fontSize: "14px",
        },

        forwardBackItem: {
            padding: theme.spacing(2),
            color: theme.palette.grey.A100,
            cursor: "pointer",
        },
        fwdBackTypo: {
            fontSize: "18px",
        },
        fwdBackIcon: {
            fontSize: "18px",
        },
        backIcon: {
            marginRight: theme.spacing(2),
        },
        fwdIcon: {
            marginLeft: theme.spacing(2),
        },
        extraIconBox: {
            marginLeft: "5px",
        },

        controlIcons: {
            minWidth: "40px",
            display: "flex",
        },
        playerIcon: {
            height: "35px",
        },
    }),
);
